/**
 * Генерация идентификаторов.
 *
 * UUIDv7 выбран потому, что он неугадываем и при этом сортируется по времени
 * создания — это даёт хорошую локальность индексов на сервере (решение AD-006).
 */

import type { InstallationId, SessionId, SyncId, UserId } from '@type2x/shared-contracts';
import type { IdFactory } from '@type2x/feature-personal-workspace';

const HEX = '0123456789abcdef';

function randomBytes(length: number): Uint8Array {
  const bytes = new Uint8Array(length);
  globalThis.crypto.getRandomValues(bytes);
  return bytes;
}

function toHex(bytes: Uint8Array): string {
  let result = '';
  for (const byte of bytes) {
    result += HEX[byte >> 4]! + HEX[byte & 0x0f]!;
  }
  return result;
}

/**
 * Создаёт UUIDv7: 48 бит времени, версия, вариант и случайный остаток.
 *
 * @param epochMs календарное время в миллисекундах.
 */
export function uuidV7(epochMs: number): string {
  const bytes = randomBytes(16);
  const timestamp = Math.max(0, Math.floor(epochMs));

  // 48 бит метки времени в старших байтах.
  bytes[0] = (timestamp / 2 ** 40) & 0xff;
  bytes[1] = (timestamp / 2 ** 32) & 0xff;
  bytes[2] = (timestamp / 2 ** 24) & 0xff;
  bytes[3] = (timestamp / 2 ** 16) & 0xff;
  bytes[4] = (timestamp / 2 ** 8) & 0xff;
  bytes[5] = timestamp & 0xff;

  // Версия 7 и вариант RFC 4122.
  bytes[6] = ((bytes[6] ?? 0) & 0x0f) | 0x70;
  bytes[8] = ((bytes[8] ?? 0) & 0x3f) | 0x80;

  const hex = toHex(bytes);
  return [
    hex.slice(0, 8),
    hex.slice(8, 12),
    hex.slice(12, 16),
    hex.slice(16, 20),
    hex.slice(20, 32),
  ].join('-');
}

/** Фабрика идентификаторов на основе UUIDv7. */
export function createIdFactory(epochMs: () => number): IdFactory {
  return {
    newInstallationId: () => uuidV7(epochMs()) as InstallationId,
    newSessionId: () => uuidV7(epochMs()) as SessionId,
    newSyncId: () => uuidV7(epochMs()) as SyncId,
    newUserId: () => uuidV7(epochMs()) as UserId,
  };
}
